// src/hooks/usePermission.ts
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectUserPermissions, selectCanPermission } from '../redux/slices/auth.slice';
import { PermissionString } from '../types/permission.types';
import { permissionsService } from '../services/permissions.service';

/**
 * Hook to check if user has a specific permission
 * @param permission - Permission string like "inventory.warehouse.adjust"
 * @returns true if user has the permission
 */
export const usePermission = (permission: PermissionString): boolean => {
  const userPermissions = useSelector(selectUserPermissions);

  return useMemo(() => {
    return permissionsService.can(userPermissions, permission);
  }, [userPermissions, permission]);
};

/**
 * Hook to check if user has any of the specified permissions
 * @param permissions - Array of permission strings
 * @returns true if user has at least one permission
 */
export const useHasAnyPermission = (permissions: PermissionString[]): boolean => {
  const userPermissions = useSelector(selectUserPermissions);

  return useMemo(() => {
    return permissionsService.hasAnyPermission(userPermissions, permissions);
  }, [userPermissions, permissions]);
};

/**
 * Hook to check if user has all of the specified permissions
 * @param permissions - Array of permission strings
 * @returns true if user has all permissions
 */
export const useHasAllPermissions = (permissions: PermissionString[]): boolean => {
  const userPermissions = useSelector(selectUserPermissions);

  return useMemo(() => {
    return permissionsService.hasAllPermissions(userPermissions, permissions);
  }, [userPermissions, permissions]);
};

/**
 * Hook to get all user permissions
 * @returns Array of permission strings
 */
export const useUserPermissions = (): PermissionString[] => {
  return useSelector(selectUserPermissions);
};

/**
 * Hook to check permission using selector (alternative approach)
 * @param permission - Permission string
 * @returns true if user has the permission
 */
export const useCan = (permission: PermissionString): boolean => {
  const canPermission = useSelector(selectCanPermission(permission));
  return canPermission;
};
